import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import {
  BusAssociation,
  BusAssociationStateTypes,
} from "./BusAssociation.util";

const name = "bus-association";
const extraActions = createExtraActions();

const BusAssociationInitialState: BusAssociationStateTypes = {
  createBusAssociation: ResetApiState(null),
  resetCreateBusAssociation: ResetApiState(null),
  editBusAssociation: ResetApiState(null),
  resetEditBusAssociation: ResetApiState(null),
  customizeBusAssociation: ResetApiState(null),
  resetCustomizeBusAssociation: ResetApiState(null),
  deleteBusAssociation: ResetApiState(null),
  resetDeleteBusAssociation: ResetApiState(null),
  busAssociations: ResetApiState([]),
  pagedBusAssociations: ResetApiState(ResetPaginationState()),
  busAssociation: ResetApiState(null),
  resetBusAssociation: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createBusAssociation = createAsyncThunk<
    ResponseWrapper<BusAssociation>,
    any
  >(`/create-${name}`, async (data: any, thunkApi) =>
    axios
      .post(`/${name}`, data)
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateBusAssociation = createAction(`/reset-create-${name}`);

  const editBusAssociation = createAsyncThunk<
    ResponseWrapper<BusAssociation>,
    any
  >(`/edit-${name}`, async ({ data, id }: any, thunkApi) =>
    axios
      .put(`/${name}/${id}`, data)
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditBusAssociation = createAction(`/reset-edit-${name}`);

  const customizeBusAssociation = createAsyncThunk<
    ResponseWrapper<boolean>,
    any
  >(`/customize-${name}`, async (data: any, thunkApi) =>
    axios
      .put(`/${name}/customize`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCustomizeBusAssociation = createAction(`/reset-customize-${name}`);

  const deleteBusAssociation = createAsyncThunk<number, any>(
    `/delete-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`${name}/${id}`)
        .then((response) => {
          if (response.data.success) {
            return id;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error?.response?.data);
        })
  );

  const resetDeleteBusAssociation = createAction(`/reset-delete-${name}`);

  const getBusAssociations = createAsyncThunk<BusAssociation[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedBusAssociations = createAsyncThunk<
    PaginationType<BusAssociation[]>,
    any
  >(`/get-paged-${name}s`, async (query: any, thunkApi) => {
    return axios
      .get(`/${name}/paged${queryStringGenerator(query)}`)
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error.message);
      });
  });

  const getBusAssociation = createAsyncThunk<BusAssociation, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}/${id}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetBusAssociation = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    createBusAssociation,
    resetCreateBusAssociation,
    editBusAssociation,
    resetEditBusAssociation,
    customizeBusAssociation,
    resetCustomizeBusAssociation,
    deleteBusAssociation,
    resetDeleteBusAssociation,
    getBusAssociations,
    getPagedBusAssociations,
    getBusAssociation,
    resetBusAssociation,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<BusAssociationStateTypes>
) {
  return {
    ...createBusAssociationReducer(),
    ...resetCreateBusAssociation(),
    ...editBusAssociationReducer(),
    ...resetEditBusAssociation(),
    ...customizeBusAssociationReducer(),
    ...resetCustomizeBusAssociation(),
    ...deleteBusAssociationReducer(),
    ...resetDeleteBusAssociation(),
    ...busAssociationsReducer(),
    ...pagedBusAssociationsReducer(),
    ...busAssociationReducer(),
    ...resetBusAssociation(),
    ...resetStatesReducer(),
  };

  function createBusAssociationReducer() {
    return {
      ...builder.addCase(
        extraActions.createBusAssociation.pending,
        (state, action) => {
          state.createBusAssociation = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createBusAssociation.fulfilled,
        (state, action) => {
          state.createBusAssociation = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createBusAssociation.rejected,
        (state, action) => {
          state.createBusAssociation = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateBusAssociation() {
    return {
      ...builder.addCase(
        extraActions.resetCreateBusAssociation,
        (state, action) => {
          state.resetCreateBusAssociation = ResetApiState(true);
          state.createBusAssociation = ResetApiState(null);
        }
      ),
    };
  }

  function editBusAssociationReducer() {
    return {
      ...builder.addCase(
        extraActions.editBusAssociation.pending,
        (state, action) => {
          state.editBusAssociation = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editBusAssociation.fulfilled,
        (state, action) => {
          state.editBusAssociation = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editBusAssociation.rejected,
        (state, action) => {
          state.editBusAssociation = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetEditBusAssociation() {
    return {
      ...builder.addCase(
        extraActions.resetEditBusAssociation,
        (state, action) => {
          state.resetEditBusAssociation = ResetApiState(true);
          state.editBusAssociation = ResetApiState(null);
        }
      ),
    };
  }

  function customizeBusAssociationReducer() {
    return {
      ...builder.addCase(
        extraActions.customizeBusAssociation.pending,
        (state, action) => {
          state.customizeBusAssociation = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.customizeBusAssociation.fulfilled,
        (state, action) => {
          state.customizeBusAssociation = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.customizeBusAssociation.rejected,
        (state, action) => {
          state.customizeBusAssociation = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCustomizeBusAssociation() {
    return {
      ...builder.addCase(
        extraActions.resetCustomizeBusAssociation,
        (state, action) => {
          state.resetCustomizeBusAssociation = ResetApiState(true);
          state.customizeBusAssociation = ResetApiState(null);
        }
      ),
    };
  }

  function deleteBusAssociationReducer() {
    return {
      ...builder.addCase(
        extraActions.deleteBusAssociation.pending,
        (state, action) => {
          state.deleteBusAssociation = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteBusAssociation.fulfilled,
        (state, action) => {
          state.deleteBusAssociation = {
            loading: false,
            payload: true,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteBusAssociation.rejected,
        (state, action) => {
          state.deleteBusAssociation = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteBusAssociation() {
    return {
      ...builder.addCase(
        extraActions.resetDeleteBusAssociation,
        (state, action) => {
          state.resetDeleteBusAssociation = ResetApiState(true);
          state.deleteBusAssociation = ResetApiState(null);
        }
      ),
    };
  }

  function busAssociationsReducer() {
    return {
      ...builder.addCase(
        extraActions.getBusAssociations.pending,
        (state, action) => {
          state.busAssociations = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getBusAssociations.fulfilled,
        (state, action) => {
          state.busAssociations = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getBusAssociations.rejected,
        (state, action) => {
          state.busAssociations = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function pagedBusAssociationsReducer() {
    return {
      ...builder.addCase(
        extraActions.getPagedBusAssociations.pending,
        (state, action) => {
          state.pagedBusAssociations = {
            loading: true,
            payload: ResetPaginationState(),
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedBusAssociations.fulfilled,
        (state, action) => {
          state.pagedBusAssociations = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedBusAssociations.rejected,
        (state, action) => {
          state.pagedBusAssociations = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function busAssociationReducer() {
    return {
      ...builder.addCase(
        extraActions.getBusAssociation.pending,
        (state, action) => {
          state.busAssociation = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getBusAssociation.fulfilled,
        (state, action) => {
          state.busAssociation = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getBusAssociation.rejected,
        (state, action) => {
          state.busAssociation = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetBusAssociation() {
    return {
      ...builder.addCase(extraActions.resetBusAssociation, (state, action) => {
        state.resetBusAssociation = ResetApiState(true);
        state.busAssociation = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createBusAssociation = ResetApiState(null);
        state.resetCreateBusAssociation = ResetApiState(null);
        state.editBusAssociation = ResetApiState(null);
        state.resetEditBusAssociation = ResetApiState(null);
        state.deleteBusAssociation = ResetApiState(null);
        state.resetDeleteBusAssociation = ResetApiState(null);
        state.busAssociations = ResetApiState([]);
        state.busAssociation = ResetApiState(null);
        state.resetBusAssociation = ResetApiState(null);
        state.resetBusAssociation = ResetApiState(null);
      }),
    };
  }
}

const BusAssociationSlice = createSlice({
  name,
  initialState: BusAssociationInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const BusAssociationActions = {
  ...BusAssociationSlice.actions,
  ...extraActions,
};
export const BusAssociationReducer = BusAssociationSlice.reducer;
